"use client"

import Script from "next/script"
import { usePathname } from "next/navigation"
import { useEffect, useRef } from "react"
import {
  clearLeadForensicsVisitorId,
  ensureLeadForensicsVisitorId,
  LEAD_FORENSICS_STORAGE_KEY,
} from "@lib/util/lead-forensics-id"

// Lead Forensics account identifier (account "807657").
const LF_ACCOUNT = "807657"
const LF_HOST = "https://www.7-visionaryinsightful.com"

declare global {
  interface Window {
    // Defined by the inline "lead-forensics-track" script below. Registers a
    // (pseudo-)page view with Lead Forensics for the given URL + title.
    track_load?: (docloc: string, doctit: string) => void
  }
}

// Fire a Lead Forensics custom event (a pseudo-page view) from anywhere in the
// app — e.g. "Item added to cart". No-op unless the LF tracker has mounted,
// which only happens once the visitor has consented to marketing cookies, so
// this is implicitly consent-gated. `url` must be a properly-formed URL or
// Lead Forensics will discard the event.
export function trackLeadForensicsEvent(url: string, title: string) {
  if (typeof window === "undefined") return
  if (typeof window.track_load !== "function") return
  window.track_load(url, title)
}

// Lead Forensics tracking.
//
// The external loader (`807657.js`) only captures the *initial* page load.
// This storefront is a Next.js App Router SPA, so navigating between pages
// happens client-side with no full page reload — meaning every page view
// after the first was being missed.
//
// Per Lead Forensics' own docs, the fix for single-page apps is to call
// `track_load(url, title)` on each navigation to register it as a pseudo-page.
// We define `track_load` here (verbatim from their docs) and fire it on every
// client-side route change *after* the first render (the loader already counts
// the first one, so calling it again would double-count the landing page).
export default function LeadForensics() {
  const pathname = usePathname()
  // The loader handles the initial page; skip the first effect run so we don't
  // double-count the landing page.
  const isInitialLoad = useRef(true)

  // Ensure the stable visitor ID exists for the lifetime of this component.
  // Component only mounts when marketing consent is granted (see
  // tracking-scripts.tsx), so ID creation is implicitly consent-gated.
  // On unmount (= consent withdrawn → tracking-scripts.tsx stops rendering
  // us), clear the stored ID so we don't retain identifiers without consent.
  useEffect(() => {
    ensureLeadForensicsVisitorId()
    return () => {
      clearLeadForensicsVisitorId()
    }
  }, [])

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false
      return
    }

    // Defer so Next.js has committed the new <title> from route metadata
    // before we read document.title.
    const id = window.setTimeout(() => {
      if (typeof window.track_load === "function") {
        window.track_load(window.location.href, document.title)
      }
    }, 0)

    return () => window.clearTimeout(id)
  }, [pathname])

  return (
    <>
      {/* Base loader — fires the initial page-load capture. */}
      <Script
        id="lead-forensics"
        src={`${LF_HOST}/js/${LF_ACCOUNT}.js`}
        strategy="afterInteractive"
      />
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          alt=""
          src={`${LF_HOST}/${LF_ACCOUNT}.png`}
          style={{ display: "none" }}
        />
      </noscript>

      {/* Custom-event tracker — defines window.track_load (adapted from the
          Lead Forensics docs) so SPA navigations can be registered. Adapted
          to read a stable per-visitor GUID from localStorage instead of
          generating a fresh one per call, so all of one visitor's events
          correlate on LF's side and can be matched against the abandoned
          cart record's pixel_identifiers.leadforensics.visitor_id. */}
      <Script id="lead-forensics-track" strategy="afterInteractive">
        {`
          function track_load (docloc, doctit) {
            var trk_sw = escape(screen.width).substring(0, 6);
            var trk_sh = escape(screen.height).substring(0, 6);
            var trk_ref = escape(document.referrer).substring(0, 1100);
            var trk_tit = escape(doctit).substring(0, 200);
            trk_tit = trk_tit.replace(/\\%u00a0/g, '');
            trk_tit = trk_tit.replace(/\\%u2122/g, '');
            trk_tit = trk_tit.replace(/\\%u[0-9][0-9][0-9][0-9]/g, '');
            var trk_loc = escape(docloc).substring(0, 200);
            var trk_agn = escape(navigator.appName).substring(0, 100);
            var trk_agv = escape(navigator.userAgent + '.lfcd' + screen.colorDepth + '.lflng').substring(0, 1000);
            var trk_dom = escape(document.domain).substring(0, 200);
            var trk_user = '${LF_ACCOUNT}';
            var trk_cookie = '';
            var trk_guid = '';
            try { trk_guid = window.localStorage.getItem('${LEAD_FORENSICS_STORAGE_KEY}') || ''; } catch (e) {}
            if (!trk_guid) {
              trk_guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                var r = Math.random()*16|0, v = c == 'x' ? r : (r&0x3|0x8);
                return v.toString(16);
              });
              try { window.localStorage.setItem('${LEAD_FORENSICS_STORAGE_KEY}', trk_guid); } catch (e) {}
            }
            var trk_img = 'https://secure.leadforensics.com/Track/Capture.aspx';
            var trk_link = trk_img + '?trk_user=' + trk_user + '&trk_sw=' + trk_sw + '&trk_sh=' + trk_sh + '&trk_ref=' + trk_ref + '&trk_tit=' + trk_tit + '&trk_loc=' + trk_loc + '&trk_agn=' + trk_agn + '&trk_agv=' + trk_agv + '&trk_dom=' + trk_dom + '&trk_guid=' + trk_guid + '&trk_cookie=NA';
            var preload = new Image();
            preload.src = trk_link;
          }
        `}
      </Script>
    </>
  )
}
